import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Radio, X } from 'lucide-react'
import { buttonVariants } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { LiveNowSession } from '@/types/api'
import { liveSessionRoomPath } from '@/features/liveSessions/liveSessionsFormat'
import { liveNowBannerText, liveNowDismissKey } from './liveNow'

interface LiveNowBannerProps {
  sessions: readonly LiveNowSession[] | undefined
  /** Prefixo da lista de aulas da area (`/app/aulas-ao-vivo` ou `/instructor/aulas-ao-vivo`). */
  basePath: string
}

/**
 * R10-12 — faixa no topo "Aula ao vivo agora: <titulo> — Entrar". Mostra a primeira aula da
 * lista (ja ao vivo primeiro). Fechar vale por aula e pela sessao do navegador. Some dentro
 * da propria sala.
 */
export function LiveNowBanner({ sessions, basePath }: LiveNowBannerProps) {
  const { pathname } = useLocation()
  const [dismissedIds, setDismissedIds] = useState<readonly number[]>([])
  const session = (sessions ?? []).find((item) => !dismissedIds.includes(item.id) && !readDismissed(item.id))
  if (!session) return null

  const roomPath = liveSessionRoomPath(basePath, session.id)
  if (pathname === roomPath || pathname.startsWith(`${roomPath}/`)) return null

  const close = () => {
    writeDismissed(session.id)
    setDismissedIds((ids) => [...ids, session.id])
  }

  return (
    <div
      role="status"
      className="mb-4 flex flex-col gap-3 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-900 sm:flex-row sm:items-center dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-100"
    >
      <Radio className="hidden h-5 w-5 flex-shrink-0 animate-pulse sm:block" aria-hidden />
      <p className="flex-1 font-semibold">{liveNowBannerText(session, new Date())}</p>
      <div className="flex items-center gap-2">
        <Link to={roomPath} className={cn(buttonVariants({ variant: 'primary', size: 'sm' }))}>
          Entrar
        </Link>
        <button
          type="button"
          onClick={close}
          aria-label="Fechar aviso de reunião ao vivo"
          className="rounded-lg p-1.5 hover:bg-red-100 dark:hover:bg-red-500/20"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  )
}

// sessionStorage pode faltar ou lancar (aba anonima, storage bloqueado): sem ele a faixa so
// deixa de ser lembrada entre telas, nunca quebra a pagina.
function readDismissed(sessionId: number): boolean {
  try {
    return window.sessionStorage.getItem(liveNowDismissKey(sessionId)) === '1'
  } catch {
    return false
  }
}

function writeDismissed(sessionId: number): void {
  try {
    window.sessionStorage.setItem(liveNowDismissKey(sessionId), '1')
  } catch {
    // Sem storage: a faixa fecha so nesta tela.
  }
}
